'use client';

import {
  Alert01Icon,
  InformationCircleIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { Portal } from '@ark-ui/react';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { LegacyDialog } from '../../components/dialog';
import { IconButton } from '../../components/icon-button';
import { Box, HStack, Stack, VStack } from '@terra/ui-styled-system';
import { automationId } from '@terra/shared-utils';
import { useEffect, useRef, type ReactNode } from 'react';

export const CONFIRMATION_DIALOG_CLOSE_REASON = {
  Confirm: 'confirm',
  Cancel: 'cancel',
  Dismiss: 'dismiss',
} as const;

export type ConfirmationDialogCloseReason =
  (typeof CONFIRMATION_DIALOG_CLOSE_REASON)[keyof typeof CONFIRMATION_DIALOG_CLOSE_REASON];

export type ConfirmationDialogVariant =
  | 'default'
  | 'destructive'
  | 'warning'
  | 'info';
export type ConfirmationDialogProps = {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  onClose?: (reason: ConfirmationDialogCloseReason) => void | Promise<void>;
  loading?: boolean;
  variant?: ConfirmationDialogVariant;
  title?: string;
  description?: string;
  descriptionContent?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  children?: ReactNode;
  showCloseButton?: boolean;
  maxWidth?: string;
  showIcon?: boolean;
  confirmDisabled?: boolean;
  indentChildren?: boolean;
  confirmAutomationId?: string;
};
const variantStyles = {
  default: {
    icon: InformationCircleIcon,
    iconColor: 'fg.neutral.default',
    confirmVariant: 'solid' as const,
  },
  destructive: {
    icon: Alert01Icon,
    iconColor: 'red.10',
    confirmVariant: 'danger' as const,
  },
  warning: {
    icon: Alert01Icon,
    iconColor: 'terra.10',
    confirmVariant: 'solid' as const,
  },
  info: {
    icon: InformationCircleIcon,
    iconColor: 'fg.neutral.default',
    confirmVariant: 'solid' as const,
  },
} as const;

export function ConfirmationDialog({
  isOpen,
  onOpenChange,
  onConfirm,
  onCancel,
  onClose,
  loading = false,
  variant = 'default',
  title,
  description,
  descriptionContent,
  confirmText,
  cancelText,
  children,
  showCloseButton = true,
  maxWidth = '400px',
  showIcon = true,
  confirmDisabled,
  indentChildren = true,
  confirmAutomationId,
}: ConfirmationDialogProps) {
  const t = useTranslations('designSystem.confirmationDialog');
  const variantConfig = variantStyles[variant];
  const IconComponent = variantConfig.icon;
  const closeReasonRef = useRef<ConfirmationDialogCloseReason | null>(null);

  useEffect(() => {
    if (isOpen) {
      closeReasonRef.current = null;
    }
  }, [isOpen]);

  const closeWithReason = async (reason: ConfirmationDialogCloseReason) => {
    if (closeReasonRef.current !== null) {
      return;
    }

    closeReasonRef.current = reason;

    try {
      if (onClose) {
        await onClose(reason);
      } else if (reason === CONFIRMATION_DIALOG_CLOSE_REASON.Confirm) {
        await onConfirm?.();
      } else if (reason === CONFIRMATION_DIALOG_CLOSE_REASON.Cancel) {
        await onCancel?.();
      }

      onOpenChange({ open: false });
    } catch (error) {
      closeReasonRef.current = null;

      throw error;
    }
  };

  const handleRootOpenChange = ({ open }: { open: boolean }) => {
    if (open) {
      onOpenChange({ open: true });

      return;
    }

    void closeWithReason(CONFIRMATION_DIALOG_CLOSE_REASON.Dismiss);
  };

  const dialogTitle = title || t('title');
  const dialogDescription =
    descriptionContent ?? description ?? t('description');
  const dialogConfirmText = confirmText || t('confirm');
  const dialogCancelText = cancelText || t('cancel');

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={handleRootOpenChange}
      lazyMount
      unmountOnExit
    >
      <Portal>
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxWidth={maxWidth}>
            <Stack gap="6" p="6">
              <VStack gap="4" alignItems="flex-start" w="full">
                <HStack gap="3" alignItems="flex-start" w="full">
                  {showIcon && (
                    <Box flexShrink={0} mt="1">
                      <IconComponent
                        size={20}
                        color={`var(--colors-${variantConfig.iconColor.replaceAll('.', '-')})`}
                      />
                    </Box>
                  )}
                  <VStack gap="2" alignItems="flex-start" flex={1}>
                    <HStack
                      gap="2"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      w="full"
                    >
                      <LegacyDialog.Title>{dialogTitle}</LegacyDialog.Title>
                      {showCloseButton && (
                        <IconButton
                          variant="ghost"
                          size="sm"
                          aria-label={t('close')}
                          onClick={() => {
                            void closeWithReason(
                              CONFIRMATION_DIALOG_CLOSE_REASON.Dismiss,
                            );
                          }}
                          disabled={loading}
                        >
                          <Cancel01Icon />
                        </IconButton>
                      )}
                    </HStack>
                    <LegacyDialog.Description>
                      {dialogDescription}
                    </LegacyDialog.Description>
                  </VStack>
                </HStack>

                {children && (
                  <Box
                    w="full"
                    pl={showIcon && indentChildren ? '8' : undefined}
                  >
                    {children}
                  </Box>
                )}
              </VStack>

              <HStack gap="3" justifyContent="flex-end" w="full">
                <Button
                  variant="outline"
                  onClick={() => {
                    void closeWithReason(
                      CONFIRMATION_DIALOG_CLOSE_REASON.Cancel,
                    );
                  }}
                  disabled={loading}
                >
                  {dialogCancelText}
                </Button>
                <Button
                  variant={variantConfig.confirmVariant}
                  onClick={() => {
                    void closeWithReason(
                      CONFIRMATION_DIALOG_CLOSE_REASON.Confirm,
                    );
                  }}
                  loading={loading}
                  disabled={loading || confirmDisabled}
                  {...(confirmAutomationId
                    ? automationId(confirmAutomationId)
                    : {})}
                >
                  {dialogConfirmText}
                </Button>
              </HStack>
            </Stack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </Portal>
    </LegacyDialog.Root>
  );
}
